import React, { useEffect, useState } from "react";
import ResponsiveImg, { SIZES } from "../components/ResponsiveImg";
import { useParams } from "react-router-dom";
import Link, { LocalizedNavigate as Navigate } from "../components/LocalizedLink";
import { useI18n } from "../i18n/I18nProvider";
import { Check } from "lucide-react";
import { api, resolveImage } from "../lib/api";
import HeroMedia, { HERO_BG } from "../components/HeroMedia";
import SchemaMarkup, { buildBreadcrumb } from "../components/SchemaMarkup";
import useDocumentMeta from "../hooks/useDocumentMeta";

const KEYS = {
  hotel: "explore-hotel",
  restaurants: "explore-restaurants",
  "beach-club": "explore-beach-club",
  spa: "explore-spa",
  "golf-course": "explore-golf-course",
};

const renderHeading = (html) => ({ __html: html || "" });

export default function ExplorePage() {
  const { area } = useParams();
  const key = KEYS[area];
  const [page, setPage] = useState(null);
  const [fetchError, setFetchError] = useState(false);
  const { t } = useI18n();
  const error = !key || fetchError;

  useEffect(() => {
    if (!key) return undefined;
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get(`/pages/${key}`);
        if (!cancel) setPage(data);
      } catch (_err) {
        if (!cancel) setFetchError(true);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [key]);

  useDocumentMeta({
    title: page?.seo?.title || page?.title || "",
    description: page?.seo?.description || "",
  });

  if (error || !key) return <Navigate to="/" replace />;
  if (!page) return <div className="min-h-screen bg-[#F9F6F0]" />;

  return (
    <div className="bg-[#F9F6F0] text-[#1A1A1A]">
      <SchemaMarkup
        schema={buildBreadcrumb([
          { name: t("home"), url: "/" },
          { name: t("explore"), url: "/explore" },
          { name: page.title, url: `/explore/${area}` },
        ])}
      />

      {/* HERO */}
      <section className={`relative h-[80svh] min-h-[520px] overflow-hidden ${HERO_BG}`}>
        <HeroMedia src={page.hero_image ? resolveImage(page.hero_image) : ""} alt={page.title} overlay="bg-gradient-to-b from-black/45 via-black/20 to-black/55" />
        <div className="relative z-10 h-full flex flex-col justify-end max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 pb-16 lg:pb-24">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#E5C99A] mb-4">
            {page.eyebrow || t("explore")}
          </p>
          <h1
            data-testid="explore-title"
            className="text-5xl sm:text-6xl lg:text-7xl text-[#F9F6F0] font-light leading-[0.95] max-w-4xl"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
            dangerouslySetInnerHTML={renderHeading(page.heading)}
          />
        </div>
      </section>

      {/* INTRO + HIGHLIGHTS */}
      <section className="py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-7">
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#73706B] mb-5">
              — {page.eyebrow || t("explore")} · {page.title}
            </p>
            <div
              className="prose prose-stone max-w-none text-lg lg:text-xl font-light leading-relaxed text-[#1A1A1A]"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
              dangerouslySetInnerHTML={{ __html: page.intro || "" }}
            />
          </div>
          <div className="lg:col-span-5">
            {page.highlights && page.highlights.length > 0 && (
              <div className="border border-[#E5DFD5] bg-[#FFFFFF] p-8">
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#73706B] mb-5">
                  {t("explore_highlights")}
                </p>
                <ul className="space-y-3">
                  {page.highlights.map((h, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check className="w-4 h-4 text-[#C5A880] mt-1 shrink-0" strokeWidth={1.5} />
                      <span className="text-base text-[#3D3B38]">{h}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* GALLERY */}
      {page.gallery && page.gallery.length > 0 && (
        <section className="pb-20 lg:pb-28">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
              {page.gallery.map((g, i) => (
                <div
                  key={i}
                  className={`relative overflow-hidden ${
                    i === 0 ? "md:col-span-2 md:row-span-2 aspect-[4/3]" : "aspect-[4/3]"
                  }`}
                >
                  <ResponsiveImg
                    src={g}
                    sizes={i === 0 ? "(min-width: 768px) 66vw, 100vw" : SIZES.third}
                    alt={`${page.title} ${i + 1}`}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1500ms]"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      {page.cta_label && page.cta_link && (
        <section className="py-20 bg-[#1A1A1A] text-[#F9F6F0]">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <h2
              className="text-3xl lg:text-4xl font-light max-w-2xl leading-tight"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              {t("begin_exploration")}
            </h2>
            <Link
              to={page.cta_link}
              data-testid="explore-cta"
              className="bg-[#C5A880] text-[#1A1A1A] px-8 py-4 text-[11px] tracking-[0.25em] uppercase hover:bg-[#F9F6F0] transition-colors"
            >
              {page.cta_label}
            </Link>
          </div>
        </section>
      )}
    </div>
  );
}
